import { useEffect, useState } from 'react';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import strings from 'constants/index';
import { KeyboardDateTimePicker } from 'components/DatePickers';

export enum PickerTypes {
  Date = 'date',
  DateTime = 'datetime',
  Time = 'time',
}

export const DEFAULT_FORMATS = {
  [PickerTypes.Date]: 'YYYY-MM-DD',
  [PickerTypes.DateTime]: 'YYYY-MM-DD HH:mm',
  [PickerTypes.Time]: 'HH:mm',
};

/** Legacy + MUI X picker props used across the app */
export interface DatePickerFormFieldProps
  extends Omit<DateTimePickerProps<any>, 'value' | 'onChange' | 'defaultValue' | 'name'> {
  pickerType?: PickerTypes;
  required?: boolean;
  size?: 'small' | 'medium';
  variant?: string;
  inputVariant?: string;
  clearable?: boolean;
  /**
   * When true, the field treats the stored value as a UTC ISO string and
   * displays/edits its wall-clock components in UTC (independent of the
   * browser timezone). On change, the picker's wall-clock is re-interpreted
   * as UTC and stored back as an ISO Z string.
   */
  utc?: boolean;
}

// Convert a UTC ISO string -> a local moment with the same wall-clock components.
// This lets the picker render the UTC time literally (e.g. 03:09Z shown as 03:09)
// regardless of the browser's local timezone.
const utcStringToDisplayMoment = (value: string) => {
  const m = moment.utc(value);
  if (!m.isValid()) {
    return null;
  }
  return moment(m.format('YYYY-MM-DDTHH:mm:ss'));
};

// Inverse of the above: take the picker's local moment and re-interpret its
// wall-clock components as UTC, returning an ISO Z string.
const displayMomentToUtcString = (date: any): string => {
  const m = moment(date);
  if (!m.isValid()) {
    return '';
  }
  return moment.utc(m.format('YYYY-MM-DDTHH:mm:ss')).toISOString();
};

export function DatePickerFormField<T extends FieldValues>({
  label,
  name,
  required,
  control,
  rules,
  format,
  pickerType = PickerTypes.DateTime,
  defaultValue,
  size = 'medium',
  variant: _variant,
  inputVariant: _inputVariant,
  clearable: _clearable,
  utc = false,
  ...datePickerProps
}: UseControllerProps<T> & DatePickerFormFieldProps) {
  const {
    field: { onChange, ref, onBlur, value },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue,
    rules: {
      required: { value: required, message: strings.field_is_required },
      ...rules,
    },
  });

  const formatValue = format || DEFAULT_FORMATS[pickerType];
  const toFieldValue = (val: any) => {
    if (!val) {
      return null;
    }
    return utc ? utcStringToDisplayMoment(val) : moment(val);
  };
  const [fieldValue, setFieldValue] = useState<any>(toFieldValue(value));
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (value) {
      setFieldValue(toFieldValue(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format, value, utc]);

  const handlePickerChange = (date: any) => {
    if (date && moment(date).isValid()) {
      setFieldError(null);
      setFieldValue(date);
      onChange(utc ? displayMomentToUtcString(date) : moment(date).format(formatValue));
    } else if (date === null) {
      setFieldError(null);
      setFieldValue(null);
      onChange(null);
    } else {
      setFieldError(strings.invalid_format_provided.replace('{0}', pickerType));
    }
  };

  const { minDate, maxDate, ...restPickerProps } = datePickerProps;
  const adjustedMinDate = toFieldValue(minDate);
  const adjustedMaxDate = toFieldValue(maxDate);

  const sharedProps = {
    ...restPickerProps,
    label,
    value: fieldValue,
    onChange: handlePickerChange,
    format: formatValue,
    ...(adjustedMinDate && { minDate: adjustedMinDate }),
    ...(adjustedMaxDate && { maxDate: adjustedMaxDate }),
    slotProps: {
      textField: {
        error: !!error || !!fieldError,
        helperText: (error?.message || fieldError) as string,
        fullWidth: true,
        size,
        required,
        inputRef: ref,
        onBlur,
        'data-testid': name,
      } as any,
    },
  };

  switch (pickerType) {
    case PickerTypes.Date:
      // DatePicker must not receive time-only props (ampm/minutesStep leak to the DOM).
      return <DatePicker {...(sharedProps as any)} />;
    case PickerTypes.Time:
      return (
        <TimePicker
          {...(sharedProps as any)}
          ampm={false}
          minutesStep={5}
          format={DEFAULT_FORMATS[PickerTypes.Time]}
        />
      );
    default:
      return (
        <KeyboardDateTimePicker {...(sharedProps as any)} ampm={false} minutesStep={5} />
      );
  }
}
